import * as Sentry from '@sentry/react';

const CORRELATION_HEADER = 'X-Correlation-Id';
const CLIENT_APP_HEADER = 'X-Client-App';
const CLIENT_VERSION_HEADER = 'X-Client-Version';

let installed = false;

const isExpectedAuthStatus = (status: number) => status === 401 || status === 403;

export function newCorrelationId(client_app: string): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${client_app}-${crypto.randomUUID()}`;
    }
  } catch (error) {}

  return `${client_app}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

interface InstallApiTracingOptions {
  client_app: string;
  client_version: string;
  api_prefixes: (string | undefined)[];
  axios_instance?: { interceptors: { request: { use: (fn: (config: any) => any) => void } } };
}

export default function installApiTracing({
  client_app,
  client_version,
  api_prefixes,
  axios_instance,
}: InstallApiTracingOptions): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  installed = true;

  const prefixes = (api_prefixes || []).filter(Boolean) as string[];

  const isTracedUrl = (url: unknown): url is string =>
    typeof url === 'string' && prefixes.some((prefix) => url.indexOf(prefix) === 0);

  const original_fetch = window.fetch.bind(window) as typeof window.fetch;

  window.fetch = function tracedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (typeof input !== 'string' || !isTracedUrl(input)) {
      // @ts-ignore — CRA's fork-ts-checker disagrees with plain `tsc` on whether
      // `original_fetch` (typed as `typeof window.fetch`) accepts `URL` here; both a
      // declared-type annotation and an explicit cast on `original_fetch` failed to
      // satisfy it. Runtime-safe regardless: it's the real `window.fetch` unbound.
      return original_fetch(input, init);
    }

    const options = init || {};
    const headers = new Headers(options.headers || {});
    const correlation_id = newCorrelationId(client_app);

    headers.set(CORRELATION_HEADER, correlation_id);
    headers.set(CLIENT_APP_HEADER, client_app);
    headers.set(CLIENT_VERSION_HEADER, client_version);

    const started_at = Date.now();

    return original_fetch(input, Object.assign({}, options, { headers }))
      .then((response) => {
        if (!response.ok && !isExpectedAuthStatus(response.status)) {
          Sentry.captureMessage('api_fetch_request_failed', {
            level: 'warning',
            tags: { correlation_id, client_app, status: String(response.status) },
            extra: { url: input, duration_ms: Date.now() - started_at },
          });
        }

        return response;
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: { correlation_id, client_app },
          extra: { url: input, duration_ms: Date.now() - started_at },
        });

        throw error;
      });
  };

  if (axios_instance && axios_instance.interceptors) {
    axios_instance.interceptors.request.use((config) => {
      const url = `${config.baseURL || ''}${config.url || ''}`;

      if (!isTracedUrl(url)) {
        return config;
      }

      config.headers = config.headers || {};
      config.headers[CORRELATION_HEADER] = newCorrelationId(client_app);
      config.headers[CLIENT_APP_HEADER] = client_app;
      config.headers[CLIENT_VERSION_HEADER] = client_version;

      return config;
    });
  }
}

export function traceAction(client_app: string, action: string, details?: Record<string, any>): string {
  const correlation_id = newCorrelationId(client_app);

  Sentry.addBreadcrumb({
    category: 'action',
    message: action,
    data: { correlation_id, client_app, ...(details || {}) },
    level: 'info',
  });

  return correlation_id;
}
